import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  FiTruck, FiPaperclip, FiAlignLeft, FiActivity, FiAlertCircle,
  FiCalendar, FiSend, FiCheck, FiInfo, FiFileText, FiMessageSquare, FiUser,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { useAuth } from '../../contexts/AuthContext';
import { useDadosVivos } from '../../hooks/useDadosVivos';
import { formatarDataBR, formatarDataHoraBR } from '../../lib/data';
import {
  fornecedorService, STATUS_OS_LABEL,
  type OrdemFornecedorDetalhe, type OrdemEventoFornecedor, type OrdemMensagemFornecedor,
} from '../../services/fornecedor.service';

const STATUS_COR: Record<OrdemFornecedorDetalhe['status'], string> = {
  enviada: 'bg-blue-50 text-blue-700 border-blue-200',
  em_execucao: 'bg-amber-50 text-amber-700 border-amber-200',
  concluida: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelada: 'bg-slate-100 text-slate-600 border-slate-200',
};
const EVENTO_LABEL: Record<OrdemEventoFornecedor['tipo'], string> = {
  criada: 'Ordem emitida', email_enviado: 'Notificação enviada por e-mail',
  reenviado: 'Notificação reenviada por e-mail', status_alterado: 'Situação atualizada', concluida: 'Ordem concluída',
};
const EVENTO_ICONE: Record<OrdemEventoFornecedor['tipo'], JSX.Element> = {
  criada: <FiTruck size={13} />, email_enviado: <FiSend size={13} />, reenviado: <FiSend size={13} />,
  status_alterado: <FiActivity size={13} />, concluida: <FiCheck size={13} />,
};
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.svg,.txt,.csv,.doc,.docx,.xls,.xlsx';
const MODULO_ROTA: Record<string, string> = {
  ouvidoria: '/ouvidoria', esic: '/esic', zeladoria: '/zeladoria',
};

type Aba = 'detalhes' | 'manifestacao' | 'timeline' | 'comunicacao';

export function FornecedorOrdemDetalhePage() {
  // Número vem como splat ("/fornecedor/*") porque contém barra (ex.: OS-0001/2026).
  const numero = useParams()['*'];
  const navigate = useNavigate();
  const { cidadao } = useAuth();
  const [aba, setAba] = useState<Aba>('detalhes');

  const { dados: ordem, carregando, erro } = useDadosVivos<OrdemFornecedorDetalhe | null>(
    async () => {
      const d = await fornecedorService.ordemDetalhe(numero!);
      if (!d) throw new Error('Ordem de serviço não encontrada ou não emitida para a sua empresa.');
      return d;
    },
    [cidadao?.id, numero],
    { ativo: !!cidadao && !!numero },
  );

  if (carregando) {
    return (
      <PortalPage titulo="Ordem de serviço" icone={<FiTruck size={26} />} largura="media" voltarHref="/fornecedor" voltarLabel="Ordens de serviço">
        <div className="flex justify-center py-20"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }
  if (erro || !ordem) {
    return (
      <PainelResultado titulo="Ordem não disponível" tom="aviso" icone={<FiAlertCircle size={32} />}
        descricao={erro ?? 'Tente novamente.'} acoes={<Button variant="outline" onClick={() => navigate('/fornecedor')}>Voltar</Button>} />
    );
  }

  const temManifestacao = !!ordem.manifestacao;
  const ABAS = ([
    { id: 'detalhes', label: 'Detalhes', mostrar: true },
    { id: 'manifestacao', label: 'Manifestação', mostrar: temManifestacao },
    { id: 'timeline', label: 'Linha do tempo', mostrar: true },
    { id: 'comunicacao', label: 'Comunicação', mostrar: true },
  ] satisfies { id: Aba; label: string; mostrar: boolean }[]).filter((a) => a.mostrar);

  return (
    <PortalPage
      titulo={ordem.titulo}
      icone={<FiTruck size={26} />}
      largura="media"
      voltarHref="/fornecedor"
      voltarLabel="Ordens de serviço"
      heroExtra={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-blue-100/90">
          {ordem.numero && <span className="tabular-nums">{ordem.numero}</span>}
          <span className="uppercase tracking-wider">· {STATUS_OS_LABEL[ordem.status]}</span>
          <span className="flex items-center gap-1"><FiCalendar size={11} /> Emitida em {formatarDataBR(ordem.criado_em)}</span>
          {ordem.prefeitura && <span>· {ordem.prefeitura}</span>}
        </div>
      }
    >
      {/* Abas */}
      <div className="mb-5 inline-flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
        {ABAS.map((a) => (
          <button key={a.id} onClick={() => setAba(a.id)}
            className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition ${aba === a.id ? 'bg-institutional text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            {a.label}
          </button>
        ))}
      </div>

      {aba === 'detalhes' && (
        <div className="space-y-5">
          <SectionCard>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Situação atual</div>
                {ordem.enviado_em && <div className="mt-1 text-xs text-slate-500">Recebida por e-mail em {formatarDataHoraBR(ordem.enviado_em)}</div>}
              </div>
              <span className={`flex-shrink-0 rounded-full border px-3 py-1 text-sm font-medium ${STATUS_COR[ordem.status]}`}>{STATUS_OS_LABEL[ordem.status]}</span>
            </div>
          </SectionCard>

          <SectionCard titulo="Descrição do serviço" icone={<FiAlignLeft size={18} />}>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{ordem.descricao ?? '—'}</p>
          </SectionCard>

          {ordem.anexo_url && (
            <SectionCard titulo="Anexo" icone={<FiPaperclip size={18} />}>
              <a href={ordem.anexo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-institutional hover:bg-slate-50">
                <FiPaperclip size={14} /> {ordem.anexo_nome ?? 'Baixar anexo'}
              </a>
            </SectionCard>
          )}
        </div>
      )}

      {aba === 'manifestacao' && ordem.manifestacao && (
        <SectionCard titulo="Manifestação de origem" icone={<FiFileText size={18} />} corpoClassName="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <FiInfo size={14} className="text-institutional" />
            <span className="font-semibold text-institutional">{ordem.manifestacao.numero ?? '—'}</span>
            {ordem.manifestacao.modulo_codigo && MODULO_ROTA[ordem.manifestacao.modulo_codigo] && (
              <Link to={MODULO_ROTA[ordem.manifestacao.modulo_codigo]} className="text-xs text-slate-400 hover:underline">
                ({ordem.manifestacao.modulo_codigo})
              </Link>
            )}
          </div>
          {ordem.manifestacao.titulo && <p className="text-sm font-medium text-slate-800">{ordem.manifestacao.titulo}</p>}
          {ordem.manifestacao.descricao && <p className="whitespace-pre-wrap text-sm text-slate-600">{ordem.manifestacao.descricao}</p>}
          <p className="text-xs text-slate-400">Esta ordem de serviço foi gerada a partir desta manifestação.</p>
        </SectionCard>
      )}

      {aba === 'timeline' && (
        <SectionCard titulo="Linha do tempo" icone={<FiActivity size={18} />}>
          {ordem.eventos.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma movimentação registrada.</p>
          ) : (
            <ul className="space-y-4">
              {ordem.eventos.map((ev) => (
                <li key={ev.id} className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-institutional">{EVENTO_ICONE[ev.tipo] ?? <FiActivity size={13} />}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="text-sm font-medium text-slate-900">{EVENTO_LABEL[ev.tipo] ?? ev.tipo}</div>
                      <div className="text-xs text-slate-500">{formatarDataHoraBR(ev.criado_em)}</div>
                    </div>
                    {ev.descricao && <div className="mt-0.5 text-sm text-slate-600">{ev.descricao}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      )}

      {aba === 'comunicacao' && <ComunicacaoFornecedor numero={numero!} />}
    </PortalPage>
  );
}

function ComunicacaoFornecedor({ numero }: { numero: string }) {
  const [mensagens, setMensagens] = useState<OrdemMensagemFornecedor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [texto, setTexto] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'erro'; msg: string } | null>(null);

  async function carregar() {
    try { setMensagens(await fornecedorService.listarMensagens(numero)); }
    catch { /* mantém vazio */ }
    finally { setCarregando(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [numero]);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!texto.trim()) return;
    setEnviando(true);
    setFeedback(null);
    try {
      let anexo: { url: string; nome: string } | undefined;
      if (arquivo) anexo = await fornecedorService.uploadAnexo(arquivo);
      await fornecedorService.enviarMensagem(numero, texto, anexo);
      setTexto(''); setArquivo(null);
      setFeedback({ tipo: 'ok', msg: 'Mensagem enviada à prefeitura.' });
      await carregar();
    } catch (err) {
      setFeedback({ tipo: 'erro', msg: err instanceof Error ? err.message : 'Falha ao enviar.' });
    } finally { setEnviando(false); }
  }

  return (
    <SectionCard titulo="Comunicação com a prefeitura" icone={<FiMessageSquare size={18} />}>
      {carregando ? <Spinner /> : mensagens.length === 0 ? (
        <p className="mb-4 text-sm text-slate-500">Nenhuma mensagem ainda. Envie a primeira abaixo.</p>
      ) : (
        <ul className="mb-4 space-y-3">
          {mensagens.map((m) => (
            <li key={m.id} className={`rounded-xl border p-3 ${m.autor_tipo === 'fornecedor' ? 'border-institutional/20 bg-institutional/5' : 'border-slate-200 bg-white'}`}>
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                <FiUser size={11} /> {m.autor_nome ?? (m.autor_tipo === 'prefeitura' ? 'Prefeitura' : 'Você')}
                <span className="font-normal text-slate-400">· {formatarDataHoraBR(m.criado_em)}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-slate-700">{m.mensagem}</p>
              {m.anexo_url && <a href={m.anexo_url} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs text-institutional hover:underline"><FiPaperclip size={11} /> {m.anexo_nome ?? 'Anexo'}</a>}
            </li>
          ))}
        </ul>
      )}

      {feedback && (
        <div className={`mb-3 rounded-lg px-3 py-2 text-sm ${feedback.tipo === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{feedback.msg}</div>
      )}

      <form onSubmit={enviar} className="space-y-2 border-t border-slate-100 pt-4">
        <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} placeholder="Escreva uma mensagem à prefeitura…"
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-institutional" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <input type="file" accept={ACCEPT} onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            className="block text-xs text-slate-600 file:mr-2 file:rounded-lg file:border-0 file:bg-slate-100 file:px-2.5 file:py-1.5 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-200" />
          <Button type="submit" loading={enviando} disabled={!texto.trim()}><FiSend size={14} /> Enviar</Button>
        </div>
      </form>
    </SectionCard>
  );
}
